import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { getTranslation } from '@/composables/data-display-utils';
import { formatAccessUntil } from '@/helpers/customerAccess.helper';
import type { CommunityDto } from '@/services/types/api.types';
import { useCommunityStore } from '@/stores';

export type CommunityMenuEntry =
  | { header: true; key: string; label: string }
  | { header: false; key: number; label: string; caption: string | null; community: CommunityDto };

// The communities a user can switch to. For a superadmin, customers and demos
// sit apart, so that entering a customer's community by mistake takes more
// than one click (COCO-3425); a customer whose access runs says until when.
export function useCommunityMenu() {
  const { t } = useI18n();
  const communityStore = useCommunityStore();

  const toEntry = (community: CommunityDto): CommunityMenuEntry => {
    const until = communityStore.customerAccessUntil(community.id);
    return {
      header: false,
      key: community.id,
      label: getTranslation(community, 'title', community.language_enabled),
      caption:
        until && !communityStore.needsCustomerRequest(community.id)
          ? t('customerAccess.openUntil', { time: formatAccessUntil(until) })
          : null,
      community,
    };
  };

  return computed<CommunityMenuEntry[]>(() => {
    const entries = communityStore.communities.map(toEntry);
    const customers = entries.filter((e) => communityStore.isCustomer(e.key as number));
    if (!customers.length) return entries;
    const byLabel = (a: CommunityMenuEntry, b: CommunityMenuEntry) => a.label.localeCompare(b.label);
    const others = entries.filter((e) => !communityStore.isCustomer(e.key as number));
    return [
      { header: true, key: 'customers', label: t('customerAccess.menu.customers') },
      ...customers.sort(byLabel),
      { header: true, key: 'others', label: t('customerAccess.menu.others') },
      ...others.sort(byLabel),
    ];
  });
}
